/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the soFftware and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FormatByOperatingLocation from '../../../../../components/FormatByOperatingLocation';
import InvoiceDownload from '../../../../invoice/InvoiceDownload';
import CancelConfirmationDialog from './components/CancelConfirmationDialog';
import EmptyState from './components/EmptyState';
import InvoiceSelectionModal from './components/InvoiceSelectorModal';
import OrderCard from './components/OrderCard';
import Snackbar from './components/Snackbar';
import SubscriptionCard from './components/SubscriptionCard';
import SupportSection from './components/SupportSection';
import useCancellation from './hooks/useCancellation';
import useDomain from './hooks/useDomain';
import useInvoice from './hooks/useInvoice';
import useMemberSubscription from './hooks/useMemberSubscription';
import useOrderData from './hooks/useOrderData';
import useOrderFilters from './hooks/useOrderFilters';
import useTimezone from './hooks/useTimezone';
import { getScheduledProductFromOrder, isOrderCashPayment, isOrderZellePayment } from './utils/orderUtils';
import { PURCHASE_AND_SUBSCRIPTION_LABEL } from '../../../../../utils/subscriptionDisplay';

const BOOKING_PROCESSING_NOTICE_KEY = 'flex_booking_processing_notice';
const BOOKING_PROCESSING_NOTICE_TTL = 10 * 60 * 1000;
const ORDER_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const ViewMyOrder = () => {
    const navigate = useNavigate();
    const companyDetails = useSelector((state) => state?.companyReducer?.company);
    const userDetail = useSelector((state) => state?.auth?.data) || {};

    // Custom hooks
    const domain = useDomain();
    const timezone = useTimezone();
    const { orders, setOrders, loading, error } = useOrderData();
    const subscriptions = useMemberSubscription(orders);
    const {
        filteredOrders,
        selectedFilter,
        setSelectedFilter,
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
    } = useOrderFilters(orders);

    const [expandedCards, setExpandedCards] = useState(new Set());
    const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'info' });
    const [bookingProcessingMessage, setBookingProcessingMessage] = useState('');
    const [bookingDateFilter, setBookingDateFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const showSnackbar = (message, type = 'info') => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: '', type: 'info' }), 5000);
    };

    const cancellation = useCancellation(orders, setOrders, showSnackbar, {
        isEducationDomain: domain.isEducationDomain
    });
    const invoice = useInvoice(companyDetails, userDetail, showSnackbar);

    const formatCurrency = (amount) => <FormatByOperatingLocation value={amount} type="CURRENCY" />;

    const toggleCardExpansion = (orderId) => {
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(orderId)) {
            newExpanded.delete(orderId);
        } else {
            newExpanded.clear();
            newExpanded.add(orderId);
        }
        setExpandedCards(newExpanded);
    };

    const canBeCancelled = (order) => {
        const status = order?.currentStatus || order?.status;
        if (!['PAID', 'PAYMENT_PENDING', 'PAYMENT_PROCESSING'].includes(status)) return false;
        if (status === 'PAID' && isOrderCashPayment(order)) return false;
        if (isOrderZellePayment(order)) return false;

        const scheduledProduct = getScheduledProductFromOrder(order);
        if (scheduledProduct?.period?.effectiveDate?.localStartDate) {
            const bookingStartTime = new Date(scheduledProduct.period.effectiveDate.localStartDate);
            return bookingStartTime > timezone.getCurrentTimeInCompanyTimezone();
        }
        return true;
    };

    const bookingDateFilteredOrders = useMemo(() => {
        if (!domain.isFlexOfficeDomain || bookingDateFilter === 'all') {
            return filteredOrders;
        }

        const companyNow = timezone.getCurrentTimeInCompanyTimezone();
        const currentCompanyDate = [
            companyNow.getFullYear(),
            String(companyNow.getMonth() + 1).padStart(2, '0'),
            String(companyNow.getDate()).padStart(2, '0')
        ].join('-');

        return filteredOrders.filter((order) => {
            const scheduledProduct = getScheduledProductFromOrder(order);
            const bookingDateTime = scheduledProduct?.period?.effectiveDate?.localStartDate
                || scheduledProduct?.effectiveStartDate
                || order?.createdDate;

            if (!bookingDateTime) {
                return bookingDateFilter === 'all';
            }

            const bookingDate = String(bookingDateTime).split('T')[0];

            if (bookingDateFilter === 'today') {
                return bookingDate === currentCompanyDate;
            }

            if (bookingDateFilter === 'future') {
                return bookingDate > currentCompanyDate;
            }

            if (bookingDateFilter === 'past') {
                return bookingDate < currentCompanyDate;
            }

            return true;
        });
    }, [bookingDateFilter, domain.isFlexOfficeDomain, filteredOrders, timezone]);

    const totalPages = Math.max(1, Math.ceil(bookingDateFilteredOrders.length / pageSize));
    const pageStartIndex = (currentPage - 1) * pageSize;
    const pageEndIndex = Math.min(pageStartIndex + pageSize, bookingDateFilteredOrders.length);
    const paginatedOrders = useMemo(
        () => bookingDateFilteredOrders.slice(pageStartIndex, pageEndIndex),
        [bookingDateFilteredOrders, pageStartIndex, pageEndIndex]
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [bookingDateFilter, pageSize, searchTerm, selectedFilter, sortBy]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (loading) return;

        const clearBookingProcessingNotice = () => {
            localStorage.removeItem(BOOKING_PROCESSING_NOTICE_KEY);
            setBookingProcessingMessage('');
        };

        const rawNotice = localStorage.getItem(BOOKING_PROCESSING_NOTICE_KEY);
        if (!rawNotice) {
            setBookingProcessingMessage('');
            return;
        }

        try {
            const notice = JSON.parse(rawNotice);
            const createdAt = Number(notice?.createdAt);
            const isExpired = !createdAt || (Date.now() - createdAt) > BOOKING_PROCESSING_NOTICE_TTL;

            const bookingExists = orders.some((order) => {
                const orderId = order?.primaryId ? String(order.primaryId) : '';
                const orderNumber = order?.number ? String(order.number) : order?.orderDetails?.[0]?.number ? String(order.orderDetails[0].number) : '';
                const parentOrderId = order?.parentOrder?.primaryId
                    ? String(order.parentOrder.primaryId)
                    : order?.parentOrderId
                        ? String(order.parentOrderId)
                        : order?.parentOrderNumber
                            ? String(order.parentOrderNumber)
                            : '';
                const parentOrderNumber = order?.parentOrder?.number
                    ? String(order.parentOrder.number)
                    : order?.parentOrderNumber
                        ? String(order.parentOrderNumber)
                        : '';

                return (notice?.orderId && orderId === String(notice.orderId))
                    || (notice?.orderId && parentOrderId === String(notice.orderId))
                    || (notice?.orderNumber && orderNumber === String(notice.orderNumber))
                    || (notice?.orderNumber && parentOrderNumber === String(notice.orderNumber));
            });

            if (isExpired || bookingExists) {
                clearBookingProcessingNotice();
                return;
            }

            setBookingProcessingMessage('Your booking is being processed. Please check back shortly.');
        } catch {
            clearBookingProcessingNotice();
        }
    }, [loading, orders]);

    // ── Loading ─────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                    <div
                        className="w-12 h-12 border-3 rounded-full animate-spin mx-auto mb-4"
                        style={{ borderColor: domain.getDomainTextColor(), borderTopColor: 'transparent' }}
                    />
                    <p className="text-slate-600 font-medium">
                        {domain.getLoadingText()}
                    </p>
                </motion.div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 p-4 flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6"
                >
                    <div className="flex items-start gap-4 mb-6">
                        <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FaExclamationTriangle className="w-5 h-5 text-rose-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-1">Error Loading Data</h3>
                            <p className="text-sm text-slate-600">{error}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition-colors font-medium"
                        style={{ backgroundColor: domain.getDomainTextColor() }}
                    >
                        Try Again
                    </button>
                </motion.div>
            </div>
        );
    }

    // ── Main render ─────────────────────────────────────────────────────────
    return (
        <>
            <div className="min-h-screen bg-slate-50" style={{ paddingTop: '60px' }}>
                <div className="max-w-6xl mx-auto px-4 py-8 fx-m-full-bleed">

                    {/* Header */}
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                        <div className="flex items-start justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                                    {domain.isFlexOfficeDomain ? 'My Bookings' :
                                        domain.isRetailDomain ? 'My Orders' :
                                            domain.isEducationDomain
                                                ? (subscriptions.subscriptionEnabled ? PURCHASE_AND_SUBSCRIPTION_LABEL : 'Purchase History') :
                                                `My ${domain.getDocumentTypeDisplay()}s`}
                                </h1>
                                <p className="text-slate-600">
                                    {domain.isFlexOfficeDomain ? 'View and manage your room reservations in one place.' :
                                        domain.isRetailDomain ? 'View and manage your product orders in one place.' :
                                            domain.isEducationDomain
                                                ? (subscriptions.subscriptionEnabled
                                                ? 'View and manage your course purchases and subscriptions in one place.'
                                                : 'View and manage your course purchases in one place.') :
                                                `Manage and track your ${domain.getDocumentTypeDisplay().toLowerCase()} history`}
                                </p>
                            </div>
                            <button
                                onClick={() => navigate(-1)}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-colors group ml-4 flex-shrink-0"
                            >
                                <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back
                            </button>
                        </div>
                    </motion.div>

                    {/* Filters */}
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="w-full sm:flex-1 sm:max-w-md">
                                <input
                                    type="text"
                                    placeholder={`Search ${domain.isFlexOfficeDomain ? 'bookings' :
                                        domain.isRetailDomain ? 'orders' :
                                            domain.isEducationDomain ? 'enrollments' :
                                                domain.getDocumentTypeDisplay().toLowerCase() + 's'
                                        }...`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="block w-full pl-4 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent"
                                    style={{ focusRingColor: domain.getDomainTextColor() }}
                                />
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                                {domain.isFlexOfficeDomain && (
                                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                                        <span className="text-sm text-slate-600 sm:whitespace-nowrap">Booking Date:</span>
                                        <select
                                            value={bookingDateFilter}
                                            onChange={(e) => setBookingDateFilter(e.target.value)}
                                            className="w-full sm:w-auto px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white"
                                            style={{ focusRingColor: domain.getDomainTextColor() }}
                                        >
                                            <option value="all">All Bookings</option>
                                            <option value="today">Today's Bookings</option>
                                            <option value="future">Future Bookings</option>
                                            <option value="past">Past Bookings</option>
                                        </select>
                                    </div>
                                )}

                                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                                    <span className="text-sm text-slate-600 sm:whitespace-nowrap">Filter:</span>
                                    <select
                                        value={selectedFilter}
                                        onChange={(e) => setSelectedFilter(e.target.value)}
                                        className="w-full sm:w-auto px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white sm:mr-4"
                                        style={{ focusRingColor: domain.getDomainTextColor() }}
                                    >
                                        <option value="all">All</option>
                                        <option value="paid">Paid</option>
                                        <option value="payment_pending">Offline Payment</option>
                                        <option value="refund_pending">Refund Pending</option>
                                        <option value="cancelled">Cancelled</option>
                                        {domain.isFlexOfficeDomain && (
                                            <>
                                                <option value="has_add_on">Has Add-On</option>
                                                <option value="has_late_fee">Has Late Fee</option>
                                            </>
                                        )}
                                    </select>
                                </div>

                                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                                    <span className="text-sm text-slate-600 sm:whitespace-nowrap">Sort by:</span>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="w-full sm:w-auto px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white"
                                        style={{ focusRingColor: domain.getDomainTextColor() }}
                                    >
                                        <option value="newest">Newest</option>
                                        <option value="oldest">Oldest</option>
                                        <option value="amount-high">Highest Amount</option>
                                        <option value="amount-low">Lowest Amount</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Results count */}
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="mb-4">
                        <p className="text-sm text-slate-600">
                            <span className="font-medium text-slate-900">{bookingDateFilteredOrders.length}</span>
                            {' '}{
                                domain.isFlexOfficeDomain ? 'booking' :
                                    domain.isRetailDomain ? 'order' :
                                        domain.isEducationDomain ? 'enrollment' :
                                            domain.getDocumentTypeDisplay().toLowerCase()
                            }{bookingDateFilteredOrders.length !== 1 ? 's' : ''} found
                        </p>
                    </motion.div>

                    {bookingProcessingMessage && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 shadow-sm"
                        >
                            <div className="flex items-start gap-3">
                                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="font-medium">{bookingProcessingMessage}</p>
                            </div>
                        </motion.div>
                    )}

                    {/* Subscription courses - only for companies that sell them */}
                    {subscriptions.subscriptionEnabled && (subscriptions.loading || subscriptions.subscriptionCourses.length > 0) && (
                        <motion.section
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.15 }}
                            className="mb-8"
                            aria-label="Subscription details"
                        >
                            <div className="flex items-baseline justify-between mb-3">
                                <h2 className="text-lg font-semibold text-slate-900">Subscription Details</h2>
                                <span className="text-sm text-slate-500">
                                    {subscriptions.loading
                                        ? 'Loading...'
                                        : `${subscriptions.subscriptionCourses.length} subscription course${subscriptions.subscriptionCourses.length === 1 ? '' : 's'}`}
                                </span>
                            </div>

                            {subscriptions.loading ? (
                                <div className="bg-white rounded-xl border border-slate-200 p-6 text-sm text-slate-600">
                                    Loading your subscription details...
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {subscriptions.subscriptionCourses.map((course, courseIndex) => (
                                        <SubscriptionCard
                                            key={course.key}
                                            course={course}
                                            index={courseIndex}
                                            formatCurrency={formatCurrency}
                                            formatDate={timezone.formatDateInCompanyTimezone}
                                            getDomainTextColor={domain.getDomainTextColor}
                                            getDomainBackgroundColor={domain.getDomainBackgroundColor}
                                            getDomainBorderColor={domain.getDomainBorderColor}
                                        />
                                    ))}
                                </div>
                            )}
                        </motion.section>
                    )}

                    {/* Purchase list */}
                    <AnimatePresence mode="wait">
                        {bookingDateFilteredOrders.length === 0 ? (
                            <EmptyState
                                selectedFilter={selectedFilter}
                                isEducationDomain={domain.isEducationDomain}
                                isFlexOfficeDomain={domain.isFlexOfficeDomain}
                                isRetailDomain={domain.isRetailDomain}
                                isInstitutionDomain={domain.isInstitutionDomain}
                                isAssessmentDomain={domain.isAssessmentDomain}
                                isServicesDomain={domain.isServicesDomain}
                                isITDomain={domain.isITDomain}
                                documentTypeDisplay={domain.getDocumentTypeDisplay()}
                                emptyStateText={domain.getEmptyStateText()}
                                exploreButtonText={domain.getExploreButtonText()}
                                exploreNavigationPath={domain.getExploreNavigationPath()}
                                onSetSelectedFilter={setSelectedFilter}
                                onNavigate={navigate}
                                domainBackgroundColor={domain.getDomainTextColor()}
                                domainIcon={domain.getDomainIcon()}
                            />
                        ) : (
                            <div className="space-y-4">
                                {paginatedOrders.map((order, index) => (
                                    <OrderCard
                                        key={order.primaryId || index}
                                        order={order}
                                        index={pageStartIndex + index}
                                        isExpanded={expandedCards.has(order.primaryId)}
                                        onToggleExpand={toggleCardExpansion}
                                        onViewReceipt={invoice.handleViewReceipt}
                                        onDownloadInvoice={invoice.handleDownloadInvoice}
                                        onCancelBooking={cancellation.initiateCancelBooking}
                                        formatCurrency={formatCurrency}
                                        formatDate={timezone.formatDateInCompanyTimezone}
                                        formatTime={timezone.formatTimeInCompanyTimezone}
                                        getTimezoneAbbreviation={timezone.getTimezoneAbbreviation}
                                        zoneId={timezone.zoneId}
                                        getDomainTextColor={domain.getDomainTextColor}
                                        getDomainBackgroundColor={domain.getDomainBackgroundColor}
                                        getDomainBorderColor={domain.getDomainBorderColor}
                                        getActionVerb={domain.getActionVerb}
                                        getDocumentTypeDisplay={domain.getDocumentTypeDisplay}
                                        isFlexOfficeDomain={domain.isFlexOfficeDomain}
                                        isRetailDomain={domain.isRetailDomain}
                                        isEducationDomain={domain.isEducationDomain}
                                        canBeCancelled={canBeCancelled}
                                        companyDetails={companyDetails}
                                        formatBookingDuration={timezone.formatBookingDuration}
                                    />
                                ))}
                            </div>
                        )}
                    </AnimatePresence>

                    {bookingDateFilteredOrders.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="mt-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                        >
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                <p className="text-sm text-slate-600">
                                    Showing <span className="font-medium text-slate-900">{pageStartIndex + 1}</span>
                                    {' '}- <span className="font-medium text-slate-900">{pageEndIndex}</span>
                                    {' '}of <span className="font-medium text-slate-900">{bookingDateFilteredOrders.length}</span>
                                </p>
                                <label className="flex items-center gap-2 text-sm text-slate-600">
                                    Per page
                                    <select
                                        value={pageSize}
                                        onChange={(event) => setPageSize(Number(event.target.value))}
                                        className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:border-transparent"
                                        style={{ focusRingColor: domain.getDomainTextColor() }}
                                    >
                                        {ORDER_PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <div className="flex items-center justify-between gap-2 sm:justify-end">
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                    disabled={currentPage === 1}
                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Previous
                                </button>

                                <span className="min-w-[92px] text-center text-sm font-medium text-slate-700">
                                    Page {currentPage} of {totalPages}
                                </span>

                                <button
                                    type="button"
                                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                    disabled={currentPage === totalPages}
                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Next
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Support section */}
                    <SupportSection
                        companyDetails={companyDetails}
                        onContactNavigate={() => navigate('/contact')}
                        getDomainTextColor={domain.getDomainTextColor}
                        isFlexOfficeDomain={domain.isFlexOfficeDomain}
                        isRetailDomain={domain.isRetailDomain}
                        isEducationDomain={domain.isEducationDomain}
                        getDocumentTypeDisplay={domain.getDocumentTypeDisplay}
                    />
                </div>

                {/* Cancel confirmation */}
                <CancelConfirmationDialog
                    show={cancellation.showCancelDialog}
                    order={cancellation.orderToCancel}
                    onClose={() => {
                        cancellation.setShowCancelDialog(false);
                        cancellation.setOrderToCancel(null);
                    }}
                    onConfirm={cancellation.confirmCancelBooking}
                    refunding={cancellation.refunding}
                    formatCurrency={formatCurrency}
                    formatDate={timezone.formatDateInCompanyTimezone}
                    formatTime={timezone.formatTimeInCompanyTimezone}
                    getTimezoneAbbreviation={timezone.getTimezoneAbbreviation}
                    zoneId={timezone.zoneId}
                    isEducationDomain={domain.isEducationDomain}
                    isRetailDomain={domain.isRetailDomain}
                />

                {/* Single invoice download */}
                {invoice.showDownloadModal && invoice.selectedInvoiceForDownload && (
                    <InvoiceDownload
                        invoice={invoice.selectedInvoiceForDownload}
                        order={invoice.selectedInvoiceForDownload.order}
                        documentType={domain.isEducationDomain ? 'Receipt' : domain.getDocumentTypeDisplay()}
                        onClose={invoice.handleCloseDownloadModal}
                    />
                )}

                {/*Multi-invoice selection modal */}
                <InvoiceSelectionModal
                    show={invoice.showSelectionModal}
                    invoices={invoice.selectionInvoices}
                    order={invoice.selectionOrder}
                    onClose={invoice.handleSelectionModalClose}
                    onView={invoice.handleSelectionView}
                    onDownload={invoice.handleSelectionDownload}
                    getDomainTextColor={domain.getDomainTextColor}
                />
            </div>

            <Snackbar
                show={snackbar.show}
                message={snackbar.message}
                type={snackbar.type}
                onClose={() => setSnackbar({ show: false, message: '', type: 'info' })}
            />
        </>
    );
};

export default ViewMyOrder;
